import React from 'react';
import 'twin.macro';
import { v4 as uuid } from 'uuid';
import Button from 'components/Buttons/Button';
import {
  getDomaineColor,
  getPeriodeColor,
} from 'containers/Programmations/ProgV2/New/getColors';
import { ArrowRightIcon as ArrowRight } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useCreateProgrammation } from 'containers/Programmations/atoms/useCreateProgrammation';
import { useTranslation } from 'react-i18next';
import { useSchoolSchoolyear } from 'containers/App/context/SchoolyearSchoolProvider';
import { Matiere } from '@teetsh/app/src/types/bo';
import { LabelledDateRange } from '@teetsh/app/src/types/zone';
import useClassrooms from 'containers/App/context/ClassroomsContext/useClassrooms';
import { Periode } from '@teetsh/app/src/types/programmations';
import { trackEvent } from 'queries/user/track';
import _capitalize from 'lodash-es/capitalize';
import { getSchoolyearWeeks } from 'containers/Programmations/ProgV2/utils/getSchoolyearWeeks';

interface Props {
  label: string;
  matiere: Matiere;
  cycle: string;
  isProgression: boolean;
}

export function buildPeriodes(periodes: LabelledDateRange[]): Periode[] {
  return periodes.map((p, index) => {
    const splitLabel = p.label.split(' ');
    let periodeNumber = splitLabel.length > 1 ? splitLabel[1] : undefined;
    // check if periodeNumber is a number or undefined
    const isNumber =
      periodeNumber !== undefined && !isNaN(parseInt(periodeNumber, 10));
    const colorIndex = isNumber ? parseInt(periodeNumber!, 10) : index;
    return {
      id: uuid(),
      position: index + 1,
      name: p.label,
      startDate: p.from,
      endDate: p.to,
      color: getPeriodeColor(colorIndex),
    };
  });
}

export function Footer(props: Props) {
  const { label, matiere, cycle, isProgression } = props;
  const navigate = useNavigate();
  const classrooms = useClassrooms();
  const { schoolyear, school } = useSchoolSchoolyear();

  const { t } = useTranslation(['programmation', 'common']);
  const createProgrammation = useCreateProgrammation();

  const goToProg = (id: string) =>
    navigate(
      `/annee-scolaire/${schoolyear.id}/ecole/${school.id}/programmations/${id}`,
    );
  const schoolyearWeeks = getSchoolyearWeeks({
    zone: schoolyear.zone,
    weekLabelFt: n => t('programmation:weekX', { count: n }),
    sundayStart: classrooms.some(c => c.workingDays.includes(-1)),
  });

  const newProgTitle = _capitalize(
    isProgression
      ? t('programmation:onboarding.progression')
      : t('programmation:onboarding.programmation'),
  );

  return (
    <div tw="pt-5 pb-8">
      <div tw="flex justify-center">
        <Button onClick={() => navigate(-1)} large>
          {t('common:buttons.cancel')}
        </Button>
        <Button
          type="button"
          onClick={() => {
            const randomMatiereId = uuid();
            const newProgId = uuid();
            createProgrammation({
              programmation: {
                id: newProgId,
                name: newProgTitle,
                columnWidth: 250,
                classroomId: (classrooms[0] || { id: '' }).id,
                invertedRowCol: false,
                cycle: cycle,
                view: props.isProgression ? 'week' : 'periode',
                hideTitle: false,
                fontSize: 'base',
                onePageMatiere: false,
                periodes: isProgression
                  ? schoolyearWeeks
                  : buildPeriodes(schoolyear.zone?.periodes || []),
                matieres: [
                  {
                    id: randomMatiereId,
                    name: matiere.nom,
                    color: 'bleu-ciel-moderate',
                    domaines: (matiere?.domaines || []).map((d, index) => ({
                      name: d.nom,
                      matiereId: randomMatiereId,
                      color: getDomaineColor(index),
                      id: uuid(),
                      items: [],
                      periodContents: [],
                    })),
                  },
                ],
              },
            }).then(() => {
              trackEvent('Programmation:startFromScratch', {
                view: props.isProgression ? 'week' : 'periode',
                cycle,
                matiere: matiere.nom,
              });
              return goToProg(newProgId);
            });
          }}
          large
          tw="ml-3"
          intent="primary"
          rightIcon={ArrowRight}
        >
          {label || t('common:buttons.create')}
        </Button>
      </div>
    </div>
  );
}
